import { Ionicons } from '@expo/vector-icons';
import { useEffect, useState } from 'react';
import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import type { FixtureWithRefs } from '@/lib/data/provider';
import { localizeTeamName } from '@/lib/i18n/footballNames';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import {
  cancelMatchReminder,
  hasMatchReminder,
  refreshMatchReminderIfScheduled,
  scheduleMatchReminder,
} from '@/lib/notifications';

/**
 * A "Remind me" toggle for an upcoming match. Schedules a purely on-device
 * local notification ~10 min before kickoff (no server / push token needed),
 * and reflects/cancels it. Hidden until we know the current state.
 */
export function MatchReminderButton({ fixture }: { fixture: FixtureWithRefs }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const palette = useThemePalette();
  const toast = useToast();
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    hasMatchReminder(fixture.id)
      .then((v) => {
        if (!alive) return;
        setOn(v);
        // If a reminder is already set and this fixture's kickoff has since moved
        // (reschedule/postponement), best-effort re-point it at the new
        // kickoff-10min so the on-device reminder stays accurate without a re-tap.
        if (v) {
          const home = localizeTeamName(fixture.home_team, locale, true);
          const away = localizeTeamName(fixture.away_team, locale, true);
          void refreshMatchReminderIfScheduled(
            fixture.id,
            fixture.kickoff_at,
            t('matchReminder.notifTitle', { home, away }),
            t('matchReminder.notifBody'),
          ).catch(() => {});
        }
      })
      .catch(() => alive && setOn(false));
    return () => {
      alive = false;
    };
    // Re-run when the kickoff changes so a refetched reschedule is picked up.
  }, [fixture.id, fixture.kickoff_at, fixture.home_team, fixture.away_team, locale, t]);

  const toggle = async () => {
    if (busy || on === null) return;
    setBusy(true);
    try {
      if (on) {
        await cancelMatchReminder(fixture.id);
        setOn(false);
        toast.show({ message: t('matchReminder.removed'), tone: 'info' });
      } else {
        const home = localizeTeamName(fixture.home_team, locale, true);
        const away = localizeTeamName(fixture.away_team, locale, true);
        const ok = await scheduleMatchReminder(
          fixture.id,
          fixture.kickoff_at,
          t('matchReminder.notifTitle', { home, away }),
          t('matchReminder.notifBody'),
        );
        setOn(ok);
        toast.show(ok ? { message: t('matchReminder.set'), tone: 'success' } : { message: t('matchReminder.failed'), tone: 'error' });
      }
    } finally {
      setBusy(false);
    }
  };

  if (on === null) return null;

  return (
    <Pressable
      onPress={toggle}
      disabled={busy}
      className="mx-4 mb-4 flex-row items-center justify-center gap-2 rounded-2xl active:opacity-80"
      style={{
        paddingVertical: 12,
        borderWidth: 1,
        borderColor: on ? palette.voltLine : palette.line,
        backgroundColor: on ? palette.voltGlass : palette.inset,
      }}
    >
      <Ionicons
        name={on ? 'notifications' : 'notifications-outline'}
        size={16}
        color={on ? palette.voltBright : palette.soft}
      />
      <AppText variant="caption" weight="bold" className={on ? 'text-volt-bright' : 'text-soft'}>
        {on ? t('matchReminder.on') : t('matchReminder.cta')}
      </AppText>
    </Pressable>
  );
}
